import React, { useState } from 'react';
import { TextField, Button, Typography } from '@mui/material';
import { CSSProperties } from 'react';
import { useStyles } from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';

// Adjusted styles to fit within the small container
const style: { [key: string]: CSSProperties } = {
  titleLabel: {
    marginLeft: '40%',
    fontSize: '1rem',
    marginBottom: '0.2rem'
  },
  calendarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '3rem'
  },
  daysContainer: {
    padding: '3px',
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginTop: '4px'
  },
  dayButton: {
    width: '20px',
    height: '20px',
    fontSize: '0.8rem',
    minWidth: '10px',
    padding: '0',
    borderRadius: '50%'
  },
  navigationButton: {
    minWidth: '30px',
    padding: '0',
    fontSize: '0.8rem'
  },
  monthLabel: {
    fontSize: '1rem'
  },
  selectedRange: {
    backgroundColor: 'rgba(0, 0, 255,)' // light blue background for selected range
  }
};

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getMonthDays = (month: number, year: number) => {
  const dates: (Date | null)[] = [];
  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0=Sun, 1=Mon...
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Add empty slots for alignment
  for (let i = 0; i < firstDayOfMonth; i++) {
    dates.push(null);
  }

  // Add actual days
  for (let day = 1; day <= daysInMonth; day++) {
    dates.push(new Date(year, month, day));
  }

  return dates;
};


interface VemigoDateRangeFieldProps {
  width: string;
  selectedSEDates: string[];
  setSelectedSEDates: React.Dispatch<React.SetStateAction<string[]>>;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: 'single' | 'range';
}

export const VemigoDateRangeField: React.FC<VemigoDateRangeFieldProps> = ({
  width,
  selectedSEDates,
  setSelectedSEDates,
  show,
  setShow,
  mode = 'range',
}) => {
  const classes = style;
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const daysInMonth = getMonthDays(currentMonth, currentYear);

  const handleDateClick = (dateString: string) => {
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    if (mode === 'single') {
      setSelectedDates([date.toLocaleDateString('en-IN')]);
      setSelectedSEDates([date.toLocaleDateString('en-IN')]);
      return;
    }

    if (selectedDates.length === 2) {
      setSelectedDates([date.toLocaleDateString('en-IN')]);
      setSelectedSEDates([date.toLocaleDateString('en-IN')]);
    } else if (selectedDates.length === 1) {
      const [day, month, year] = selectedDates[0].split('/').map(Number);
      const firstDate = new Date(year, month - 1, day);
      const datesBetween: string[] = [];
      const startDate = firstDate < date ? firstDate : date;
      const endDate = firstDate < date ? date : firstDate;

      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        datesBetween.push(currentDate.toLocaleDateString('en-IN'));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setSelectedDates([...selectedDates, ...datesBetween]);
      setSelectedSEDates([
        startDate.toLocaleDateString('en-IN'),
        endDate.toLocaleDateString('en-IN')
      ]);
    } else {
      setSelectedDates([date.toLocaleDateString('en-IN')]);
      setSelectedSEDates([date.toLocaleDateString('en-IN')]);
    }
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleClear = () => {
    setSelectedDates([]);
    setSelectedSEDates([]);
  };

  return (
    <>
      <TextField
        style={{ ...useStyles.formControl, width: `${width}` }}
        value={selectedSEDates.join('   -   ')}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div
          style={{
            position: 'absolute',
            width: '250px',
            marginTop: '3px',
            overflow: 'auto',
            borderRadius: '3px',
            boxShadow: '0px 0px 1px rgb(0, 0, 0, 0.9)',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            padding: '3px',
            zIndex: 1000
          }}
        >
          <Typography variant="h6" style={classes.titleLabel}>
            Dates
          </Typography>
          <div style={classes.calendarContainer}>
            <Button onClick={handlePreviousMonth} style={classes.navigationButton}>
              {'<'}
            </Button>
            <Typography style={classes.monthLabel}>
              {`${new Date(currentYear, currentMonth).toLocaleString('default', { month: 'short' })} ${currentYear}`}
            </Typography>
            <Button onClick={handleNextMonth} style={classes.navigationButton}>
              {'>'}
            </Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '4px' }}>
            {weekDays.map((day, idx) => (
              <Typography key={idx} style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                {day}
              </Typography>
            ))}
          </div>
          <div style={classes.daysContainer}>
            {daysInMonth.map((date, index) =>
              date ? (
                <Button
                  key={index}
                  variant={
                    selectedDates.includes(date.toLocaleDateString('en-IN'))
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={() => handleDateClick(date.toLocaleDateString('en-IN'))}
                  style={{
                    ...classes.dayButton,
                    ...(selectedDates.includes(date.toLocaleDateString('en-IN')) && classes.selectedRange)
                  }}
                >
                  {date.getDate()}
                </Button>
              ) : (
                <div key={index} /> // empty slot
              )
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button style={{ minWidth: '20px', padding: '0', fontSize: '0.9rem' }} onClick={handleClear}>
              clear
            </Button>
            <Button style={{ minWidth: '20px', padding: '0', fontSize: '0.9rem' }} onClick={() => setShow(false)}>
              x
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

